import React from "react";

import { Download, Link as LinkIcon, Pencil } from "lucide-react";
import moment from "moment";

import { DATE_FORMAT } from "@/src/constants/dateFormat";

import DataTableColumnHeader from "../../dataTable/DataTableColumnHeader";
import {
  actionsColumn,
  dateColumn,
  enabledStatusOptions,
  statusColumn,
} from "../../dataTable/dataTableColumns";

import type { TFunction } from "i18next";

import type { FreecopyUniqueCodeRow } from "./freecopyUniqueCodeColumns.types";
import type { DataTableColumnDef } from "../../dataTable/dataTable.types";

const TEXT_MIN_WIDTH = 150;

interface FreecopyUniqueCodeColumnsOptions {
  t: TFunction;
  canWrite: boolean;
  onLink: (code: FreecopyUniqueCodeRow) => void;
  onEdit: (code: FreecopyUniqueCodeRow) => void;
  onExport: (code: FreecopyUniqueCodeRow) => void;
}

export function freecopyUniqueCodeColumns({
  t,
  canWrite,
  onLink,
  onEdit,
  onExport,
}: FreecopyUniqueCodeColumnsOptions): DataTableColumnDef<FreecopyUniqueCodeRow>[] {
  return [
    statusColumn<FreecopyUniqueCodeRow>(
      "status",
      t("status"),
      enabledStatusOptions(t),
    ),
    {
      accessorKey: "label",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("labelUniqueCode")} />
      ),
      meta: {
        searchable: true,
        label: t("labelUniqueCode"),
        minWidth: TEXT_MIN_WIDTH,
      },
      cell: ({ row }) => row.original.label,
    },
    {
      accessorKey: "code",
      enableHiding: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("token")} />
      ),
      meta: {
        searchable: true,
        label: t("token"),
        width: "9%",
        minWidth: 105,
        align: "center",
      },
      cell: ({ row }) => (
        <span className="text-muted-foreground font-mono">
          {row.original.code}
        </span>
      ),
    },
    {
      accessorKey: "offer_name",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("offer")} />
      ),
      meta: { searchable: true, label: t("offer"), minWidth: TEXT_MIN_WIDTH },
      cell: ({ row }) => row.original.offer_name,
    },
    {
      accessorKey: "current_activations",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("activationsOffMax")} />
      ),
      meta: {
        label: t("activationsOffMax"),
        width: "11%",
        minWidth: 120,
        align: "center",
      },
      cell: ({ row }) =>
        `${row.original.current_activations}/${row.original.max_activations}`,
    },
    {
      id: "accessDuration",
      accessorFn: ({ usage_limit_date: limitDate, days_duration: days }) => {
        if (limitDate) return moment(limitDate).format(DATE_FORMAT);
        return days ? `${days} ${t("days")}` : "";
      },
      enableHiding: true,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("durationDateAccess")}
        />
      ),
      enableSorting: false,
      meta: {
        label: t("durationDateAccess"),
        width: "8%",
        minWidth: 95,
      },
    },
    {
      ...dateColumn<FreecopyUniqueCodeRow>(
        "activation_limit_date",
        t("limitActivation"),
        { width: "13%", minWidth: 155 },
      ),
      enableHiding: true,
    },
    {
      ...dateColumn<FreecopyUniqueCodeRow>("inserted_at", t("insertedAt"), {
        width: "12%",
        minWidth: 135,
      }),
      enableHiding: true,
    },
    {
      ...dateColumn<FreecopyUniqueCodeRow>("updated_at", t("update_date"), {
        width: "11%",
        minWidth: 125,
      }),
      enableHiding: true,
    },
    actionsColumn<FreecopyUniqueCodeRow>(
      (code) => [
        { label: t("link"), icon: <LinkIcon />, onClick: () => onLink(code) },
        {
          label: t("edit"),
          icon: <Pencil />,
          hidden: !canWrite,
          onClick: () => onEdit(code),
        },
        {
          label: t("export"),
          icon: <Download />,
          onClick: () => onExport(code),
        },
      ],
      (code) => code.label,
    ),
  ];
}
